// src/components/staff/StaffImpersonationBanner.jsx
import { useWmStaff } from '../../context/WmStaffContext';
import { useAuditLog } from '../../context/AuditLogContext';
import { useEffect } from 'react';

const ROLE_STYLES = {
  senior: {
    border: 'rgba(245,158,11,0.45)',
    dot: '#F59E0B',
    dotGlow: 'rgba(245,158,11,0.5)',
    text: '#FCD34D',
    btnColor: '#F59E0B',
    btnBg: 'rgba(245,158,11,0.12)',
    btnBorder: 'rgba(245,158,11,0.3)',
    label: () => `WrapMind Staff — viewing`,
  },
  support: {
    border: 'rgba(46,139,240,0.45)',
    dot: '#2E8BF0',
    dotGlow: 'rgba(46,139,240,0.5)',
    text: '#60ABFF',
    btnColor: '#2E8BF0',
    btnBg: 'rgba(46,139,240,0.12)',
    btnBorder: 'rgba(46,139,240,0.3)',
    label: () => `WrapMind Support — read-only ·`,
  },
  account_manager: {
    border: 'rgba(16,185,129,0.45)',
    dot: '#10B981',
    dotGlow: 'rgba(16,185,129,0.5)',
    text: '#34D399',
    btnColor: '#10B981',
    btnBg: 'rgba(16,185,129,0.12)',
    btnBorder: 'rgba(16,185,129,0.3)',
    label: () => `WrapMind Account Manager — viewing`,
  },
};

export default function StaffImpersonationBanner({ bottomOffset = 0 }) {
  const { isWmStaff, wmRole, wmStaffMember, viewingOrgId, viewingOrgName, exitOrgView } = useWmStaff();
  const { addLog } = useAuditLog();

  // Audit: log entry when impersonation starts
  useEffect(() => {
    if (!viewingOrgId || !wmStaffMember) return;
    addLog('STAFF', 'ENTER_ORG_VIEW', {
      severity: 'info',
      actor: { role: 'wm_staff', label: `staff:${wmStaffMember.email}` },
      details: { orgId: viewingOrgId, orgName: viewingOrgName, wmRole },
    });
  }, [viewingOrgId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isWmStaff || !viewingOrgId) return null;

  const style = ROLE_STYLES[wmRole] ?? ROLE_STYLES.support;

  const handleExit = () => {
    addLog('STAFF', 'EXIT_ORG_VIEW', {
      severity: 'info',
      actor: { role: 'wm_staff', label: `staff:${wmStaffMember.email}` },
      details: { orgId: viewingOrgId, orgName: viewingOrgName, wmRole },
    });
    exitOrgView();
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24 + bottomOffset,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9996,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        background: 'rgba(15,25,35,0.92)',
        border: `1px solid ${style.border}`,
        borderRadius: 999,
        padding: '7px 14px 7px 12px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(12px)',
        userSelect: 'none',
        pointerEvents: 'auto',
      }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: '50%',
        background: style.dot,
        boxShadow: `0 0 6px 2px ${style.dotGlow}`,
        flexShrink: 0,
      }} />
      <span style={{ fontSize: 'var(--wm-text-ui)', color: '#F8FAFE', fontWeight: 500, whiteSpace: 'nowrap' }}>
        {style.label(viewingOrgName)}{' '}
        <strong style={{ color: style.text }}>{viewingOrgName}</strong>
      </span>
      <button
        onClick={handleExit}
        style={{
          marginLeft: 4,
          fontSize: 'var(--wm-text-meta)',
          fontWeight: 600,
          color: style.btnColor,
          background: style.btnBg,
          border: `1px solid ${style.btnBorder}`,
          borderRadius: 999,
          padding: '3px 10px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.opacity = '0.8'}
        onMouseLeave={e => e.currentTarget.style.opacity = '1'}
      >
        Exit
      </button>
    </div>
  );
}
